import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import FileUpload from "components/common/FileUpload";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import FormShimmer from "components/common/shimmer/FormShimmer ";
import CustomField from "components/form/CustomField";
import { Form, Formik } from "formik";
import { appendFormData, handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addBanner,
  getBannerDetails,
  updateBanner,
} from "store/slices/bannersSlice";
import { getBannerValidationSchema } from "utils/validations";

const AddEditBanner = ({ mode }) => {
  const { t } = useTranslation();
  const id = useQueryParam("id");
  const isEdit = mode === "edit";
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canEdit, canAdd } = canAction("banners");

  const { isLoading, loading, itemDetails } = useSelector(
    (state) => state.Banners,
  );

  const [initialValues, setInitialValues] = useState({
    title: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
    image: null,
    // sortOrder: null,
  });

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getBannerDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  useEffect(() => {
    if (isEdit && itemDetails) {
      const { imageUrl, title, description, sortOrder } = itemDetails;

      setInitialValues({
        title: {
          en: title?.en || "",
          ar: title?.ar || "",
        },
        description: {
          en: description?.en || "",
          ar: description?.ar || "",
        },
        image: imageUrl || null,
        // sortOrder: sortOrder ?? null,
      });
    }
  }, [isEdit, itemDetails]);

  const handleSubmit = (values) => {
    const data = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      appendFormData(data, key, value);
    });
    if (isEdit) {
      data.append("id", id);

      handleSubmitForm({
        dispatch,
        action: updateBanner,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addBanner,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    }
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading) {
    return <AddEditLoader title={t("Edit Banner")} />;
  }

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Banner") : t("Add Banner")}
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getBannerValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (English)")}
                      name="title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="title.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (Arabic)")}
                      name="title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="title.en"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (English)")}
                      name="description.en"
                      placeholder={t("Enter Description (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="description.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (Arabic)")}
                      name="description.ar"
                      placeholder={t("Enter Description (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="description.en"
                    />
                  </div>
                </Col>

                {/* {isEdit && (
                  <Col lg={6}>
                    <div className="form_field">
                      <CustomField
                        label={t("Sort Order")}
                        name="sortOrder"
                        type="number"
                      />
                    </div>
                  </Col>
                )} */}
                <Col lg={12}>
                  <FileUpload
                    uploadedFiles={values.image || null}
                    required
                    label={t("Upload Image")}
                    setUploadedFiles={(file) => setFieldValue("image", file)}
                    multiple={false}
                    maxFiles={1}
                    maxFileSize={0.6 * 1024 * 1024}
                    acceptedFileTypes={["image/png", "image/jpeg"]}
                    className={touched?.image && errors.image ? "invalid" : ""}
                  />
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Add Banner")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditBanner;
